import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Zap, Phone, Globe, Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useQuoteModal } from "@/components/QuoteModal";
import { SITE } from "@/data/site";

const Links = () => {
  const { t } = useLang();
  const cls = ({ isActive }) =>
    `text-sm font-medium transition-colors duration-200 hover:text-electric ${
      isActive ? "text-electric" : "text-slate-600"
    }`;
  return (
    <>
      <NavLink to="/" className={cls} data-testid="nav-home-link">{t.nav.home}</NavLink>
      <NavLink to="/services" className={cls} data-testid="nav-services-link">{t.nav.services}</NavLink>
      <NavLink to="/projects" className={cls} data-testid="nav-projects-link">{t.nav.projects}</NavLink>
      <NavLink to="/contact" className={cls} data-testid="nav-contact-link">{t.nav.contact}</NavLink>
    </>
  );
};

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const { open } = useQuoteModal();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const mobileNav =
    "px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-electric transition-colors duration-200";

  return (
    <header className="sticky top-0 z-50 glass shadow-card" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="nav-logo" aria-label="Nextron home">
            <span className="w-9 h-9 rounded-lg bg-navy flex items-center justify-center">
              <Zap className="w-5 h-5 text-amber2" fill="currentColor" />
            </span>
            <span className="leading-tight">
              <span className="block font-display font-extrabold tracking-tight text-navy text-lg">NEXTRON</span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 -mt-0.5">
                {SITE.tagline}
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            <Links />
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggle}
              data-testid="nav-lang-toggle"
              className="flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-navy hover:border-electric hover:text-electric transition-colors duration-200"
              aria-label="Switch language"
            >
              <Globe className="w-3.5 h-3.5" />
              {lang === "en" ? "हिंदी" : "EN"}
            </button>
            <a
              href={SITE.phoneHref}
              data-testid="nav-call-button"
              className="hidden sm:flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs sm:text-sm font-semibold text-white hover:bg-royal transition-colors duration-200"
            >
              <Phone className="w-4 h-4" />
              {t.nav.call}
            </a>
            <button
              onClick={open}
              data-testid="nav-quote-button"
              className="hidden lg:block rounded-full bg-electric px-5 py-2 text-sm font-semibold text-white hover:bg-blue-700 active:scale-[0.98] transition-all duration-200"
            >
              {t.nav.quote}
            </button>
            <button
              className="md:hidden p-2 rounded-lg text-navy hover:bg-blue-50 transition-colors"
              onClick={() => setMobileOpen((o) => !o)}
              data-testid="nav-mobile-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-slate-100 bg-white px-4 pt-2 pb-4 space-y-1" data-testid="nav-mobile-menu">
          {[
            { to: "/", label: t.nav.home, id: "nav-mobile-home" },
            { to: "/services", label: t.nav.services, id: "nav-mobile-services" },
            { to: "/projects", label: t.nav.projects, id: "nav-mobile-projects" },
            { to: "/contact", label: t.nav.contact, id: "nav-mobile-contact" },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={mobileNav + (location.pathname === l.to ? " bg-blue-50 text-electric" : "")}
              onClick={() => setMobileOpen(false)}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={() => { setMobileOpen(false); open(); }}
            data-testid="nav-mobile-quote-button"
            className="w-full mt-2 rounded-full bg-electric px-5 py-2.5 text-sm font-semibold text-white active:scale-[0.98] transition-transform"
          >
            {t.nav.quote}
          </button>
        </div>
      )}
    </header>
  );
}
